<script setup lang="ts">
import OverlayBase from './OverlayBase.vue'
import TwoColsBlock from '../layout/TwoColsBlock.vue'
import SkrButton from '../button/SkrButton.vue'

const props = defineProps<{
  planType: 'TEAM_V1' | 'PRO_V1'
}>()
const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()
const { mdAndUp } = useDisplay()
const { upgradeTrial } = usePaymentStore()
const { loadUserBusinessData } = useUserStore()
const { updateContactInfo } = useBusinessStore()
const widgetStore = useWidgetStore()
const { logError } = useErrorHandling()

const { track } = useAnalytics()

onMounted(() => {
  track(ANALYTICS_EVENTS.UPGRADE_PROMPT_VIEWED)
})

const teamName = ref('')
const agreedToTerms = ref(false)
const isLoading = ref(false)
const isFormValid = computed(() => Boolean(teamName.value.trim()))
const planName = computed(() => (props.planType === 'PRO_V1' ? 'pro' : 'team'))
const title = computed(() =>
  planName.value === 'pro' ? t('starter.upgrade.title.pro') : t('starter.upgrade.title.team')
)
const ctaText = computed(() =>
  planName.value === 'pro' ? t('starter.upgrade.start_trial.pro') : t('starter.upgrade.start_trial.team')
)

const startTrial = async () => {
  if (!props.planType || !isFormValid.value) return

  isLoading.value = true
  try {
    await updateContactInfo({ name: teamName.value.trim() })
    await upgradeTrial(props.planType)
    await loadUserBusinessData()
    track(ANALYTICS_EVENTS.STARTER_TRIAL_ACTIVATED, { planType: props.planType })
    closeOverlay()
    navigateTo({ name: 'index' })
    widgetStore.createSnackbar({ message: t('starter.upgrade.trial_activated') })
  } catch (e) {
    logError('Failed to start trial', e)
    widgetStore.createSnackbar({
      message: t('global.error.generic_support'),
    })
  } finally {
    isLoading.value = false
  }
}

const closeOverlay = () => {
  emit('close')
}

const onDismiss = () => {
  track(ANALYTICS_EVENTS.UPGRADE_PROMPT_DECLINED)
  closeOverlay()
}
</script>
<template>
  <overlay-base>
    <template #header>
      <toolbar-base dismissible @close="onDismiss"></toolbar-base>
    </template>
    <template #default>
      <two-cols-block split="5/7" class="starter-trial fill-height" data-cy="starter_trial_form">
        <template #left>
          <skr-heading level="1" class="starter-trial__title">
            {{ title }}
          </skr-heading>
          <v-form class="starter-trial__form mt-12" @submit.prevent="startTrial">
            <v-text-field
              v-model="teamName"
              data-cy="starter_trial_team_input"
              autocomplete="organization"
              :label="t('starter.upgrade.name_label')"
              name="teamName"
              variant="filled"
              :rules="[value => !isEmptyString(value) || t('starter.upgrade.name_error')]"
              class="starter-trial__team-name mb-4"
            />
            <skr-checkbox v-model="agreedToTerms" data-cy="legal_checkbox" class="mb-8">
              <i18n-t keypath="business.upgrade.plan.legal" class="starter-trial__legal-checkbox">
                <template #gtc>
                  <a :href="t('business.setup.gtc_dpa.gtc_link_url')" target="_blank" rel="noopener" @click.stop>
                    {{ t('business.setup.gtc_dpa.gtc_link_text') }}
                  </a>
                </template>
                <template #dpa>
                  <a
                    :href="t('business.setup.gtc_dpa.dpa_link_url')"
                    target="_blank"
                    rel="noopener,nofollow"
                    @click.stop
                  >
                    {{ t('business.setup.gtc_dpa.dpa_link_text') }}
                  </a>
                </template>
              </i18n-t>
            </skr-checkbox>

            <skr-button
              data-cy="trial_submit_button"
              :loading="isLoading"
              :disabled="!isFormValid || !agreedToTerms || isLoading"
              size="lg"
              color="black"
              class="starter-trial__button"
              @click="startTrial"
            >
              {{ ctaText }}
            </skr-button>
          </v-form>
        </template>
        <template v-if="mdAndUp" #right>
          <div class="info-cards-wrapper">
            <div class="info-cards">
              <v-icon class="info-cards__dots" size="150">custom:dots</v-icon>
              <div class="reassurance-card" :class="`reassurance-card--${planName}`">
                <v-icon size="40" class="reassurance-card__icon">custom:no_credit_card</v-icon>
                <div class="reassurance-card__info">
                  <skr-heading level="3" class="reassurance-card__title">{{
                    t('starter.upgrade.reassurance.commitment.title')
                  }}</skr-heading>
                  <div class="reassurance-card__description">
                    {{ t('starter.upgrade.reassurance.commitment.description') }}
                  </div>
                </div>
              </div>
              <nuxt-img
                src="/img/vertical-lines.svg"
                width="140"
                height="140"
                class="info-cards__vertical-lines"
              ></nuxt-img>
              <div class="reassurance-card" :class="`reassurance-card--${planName}`">
                <v-icon size="40" class="reassurance-card__icon">custom:upgrade_arrow</v-icon>
                <div class="reassurance-card__info">
                  <skr-heading level="3" class="reassurance-card__title">{{
                    t('starter.upgrade.reassurance.cancel_upgrade.title')
                  }}</skr-heading>
                  <div class="reassurance-card__description">
                    {{ t('starter.upgrade.reassurance.cancel_upgrade.description') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </two-cols-block>
    </template>
  </overlay-base>
</template>

<style lang="scss" scoped>
.starter-trial {
  &__form {
    width: 100%;
  }

  &__title {
    color: $c-black;
    font-size: 2.5rem;
    font-weight: 800;
  }

  &__button,
  &__team-name {
    width: 100%;
  }

  &__legal-checkbox {
    text-shadow: none;
  }
}

.info-cards {
  position: relative;
  display: grid;
  grid-template-rows: auto 36px auto;

  .between {
    position: relative;
  }

  &-wrapper {
    padding-left: 38 * $bw;
  }

  &__dots {
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(37%, -44%);
    pointer-events: none;
    color: $c-grey-medium-4;
  }

  &__vertical-lines {
    position: relative;
    left: 0;
    top: 50%;
    transform: translate(-70%, -50%);
    z-index: 4;
    pointer-events: none;
    color: $c-black;
  }
}

.reassurance-card {
  position: relative;
  display: flex;
  align-items: start;
  gap: 9 * $bw;
  padding: 10 * $bw;
  border-radius: 5 * $bw;
  color: $c-black;

  &--pro {
    background-color: $c-blue-light;
  }

  &--team {
    background-color: $c-yellow-light;
  }

  &__title {
    font-size: 1.25rem;
    font-weight: 800;
    margin-bottom: 2 * $bw;
  }
}

:deep(.skr-checkbox .v-selection-control) {
  align-items: start !important;
}

:deep(.v-selection-control__wrapper) {
  margin-top: 5px;
}
</style>
